import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useAuthStore } from '../store/authStore';
import { useTeamStore } from '../store/teamStore';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { toast } from 'react-hot-toast';
import { Mail, UserPlus, X, Check, Loader2, Edit2, Trash2 } from 'lucide-react';
import { piiService } from '../services/piiService';
import { MobileSelect } from './ui/MobileSelect';

type CoachInvitation = {
  id: string;
  team_id: string;
  email: string;
  role: 'head_coach' | 'assistant_coach';
  status: 'pending' | 'accepted' | 'rejected';
  created_at: string;
  invited_by: string;
};

type Coach = {
  id: string;
  team_id: string;
  user_id: string;
  role: 'head_coach' | 'assistant_coach';
  name: string;
  email: string;
  phone?: string;
};

const getInviteErrorMessage = async (error: unknown): Promise<string> => {
  if (!(error instanceof FunctionsHttpError)) {
    return 'Failed to send invitation';
  }

  try {
    const payload = await error.context.json();
    const details =
      (payload && typeof payload === 'object' && 'details' in payload && typeof payload.details === 'string' && payload.details) ||
      (payload && typeof payload === 'object' && 'message' in payload && typeof payload.message === 'string' && payload.message) ||
      (payload && typeof payload === 'object' && 'error' in payload && typeof payload.error === 'string' && payload.error) ||
      null;

    if (details) {
      // Keep message user-friendly while still exposing root cause.
      return `Invitation was created, but email failed: ${details}`;
    }
  } catch (parseError) {
    console.warn('Unable to parse edge function error response:', parseError);
  }

  return 'Invitation was created, but email failed to send';
};

export function CoachInvitation() {
  const { currentTeam } = useTeamStore();
  const { user } = useAuthStore();
  const ownerCoachCheckRef = useRef<Set<string>>(new Set());
  const [invitations, setInvitations] = useState<CoachInvitation[]>([]);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<'head_coach' | 'assistant_coach'>('assistant_coach');
  const [isInviting, setIsInviting] = useState(false);
  const [editingCoach, setEditingCoach] = useState<string | null>(null);
  const [addCoachForm, setAddCoachForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'assistant_coach' as 'head_coach' | 'assistant_coach'
  });
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'assistant_coach' as 'head_coach' | 'assistant_coach'
  });

  // Validation states
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [addEmailError, setAddEmailError] = useState('');
  const [addPhoneError, setAddPhoneError] = useState('');
  const [editEmailError, setEditEmailError] = useState('');
  const [editPhoneError, setEditPhoneError] = useState('');

  useEffect(() => {
    if (currentTeam?.id) {
      fetchInvitations();
      fetchCoaches();
    }
  }, [currentTeam?.id]);

  // Listen for force refresh events (e.g., after invitation acceptance)
  useEffect(() => {
    const handleForceRefresh = () => {
      console.log('[CoachInvitation] Received force-team-refresh event, refreshing invitations and coaches...');
      if (currentTeam?.id) {
        fetchInvitations();
        fetchCoaches();
      }
    };

    window.addEventListener('force-team-refresh', handleForceRefresh);
    return () => window.removeEventListener('force-team-refresh', handleForceRefresh);
  }, [currentTeam?.id]);

  // Add useEffect to ensure team owner is also a head coach
  useEffect(() => {
    const ensureTeamOwnerIsCoach = async () => {
      if (!currentTeam?.id || !user) return;
      const checkKey = `${currentTeam.id}:${user.id}`;
      if (ownerCoachCheckRef.current.has(checkKey)) return;
      ownerCoachCheckRef.current.add(checkKey);

      try {
        // Check if user is already a coach
        const { data: existingCoach, error: coachError } = await supabase
          .from('coaches')
          .select('id')
          .eq('team_id', currentTeam.id)
          .eq('user_id', user.id)
          .limit(1)
          .maybeSingle();

        if (coachError && coachError.code !== 'PGRST116') { // PGRST116 is "no rows returned"
          console.error('Error checking coach status:', coachError);
          return;
        }

        // If user is not a coach, add them as head coach
        if (!existingCoach) {
          const { error: insertError } = await supabase
            .from('coaches')
            .insert({
              team_id: currentTeam.id,
              user_id: user.id,
              name: user.email?.split('@')[0] || 'Team Owner',
              role: 'head_coach',
              position: 'Head Coach'
            });

          if (insertError) {
            console.error('Error adding team owner as coach:', insertError);
            return;
          }

          // Refresh coaches list
          fetchCoaches();
        }
      } catch (error) {
        console.error('Error in ensureTeamOwnerIsCoach:', error);
      }
    };

    ensureTeamOwnerIsCoach();
  }, [currentTeam?.id, user]);

  // Validation functions
  const validateEmail = (emailToCheck: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailToCheck) {
      setEmailError('Email is required');
      return false;
    } else if (!emailRegex.test(emailToCheck)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validateEditEmail = (emailToCheck: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailToCheck) {
      setEditEmailError('Email is required');
      return false;
    } else if (!emailRegex.test(emailToCheck)) {
      setEditEmailError('Please enter a valid email address');
      return false;
    }
    setEditEmailError('');
    return true;
  };

  const validateAddEmail = (emailToCheck: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailToCheck) {
      setAddEmailError('Email is required');
      return false;
    } else if (!emailRegex.test(emailToCheck)) {
      setAddEmailError('Please enter a valid email address');
      return false;
    }
    setAddEmailError('');
    return true;
  };

  const validatePhone = (phoneToCheck: string): boolean => {
    if (!phoneToCheck) {
      // Phone is optional
      setPhoneError('');
      return true;
    }
    
    const digitsOnly = phoneToCheck.replace(/\D/g, '');
    if (digitsOnly.length !== 10) {
      setPhoneError('Phone number must be 10 digits');
      return false;
    }
    
    setPhoneError('');
    return true;
  };

  const validateEditPhone = (phoneToCheck: string): boolean => {
    if (!phoneToCheck) {
      // Phone is optional
      setEditPhoneError('');
      return true;
    }
    
    const digitsOnly = phoneToCheck.replace(/\D/g, '');
    if (digitsOnly.length !== 10) {
      setEditPhoneError('Phone number must be 10 digits');
      return false;
    }
    
    setEditPhoneError('');
    return true;
  };

  const validateAddPhone = (phoneToCheck: string): boolean => {
    if (!phoneToCheck) {
      setAddPhoneError('');
      return true;
    }

    const digitsOnly = phoneToCheck.replace(/\D/g, '');
    if (digitsOnly.length !== 10) {
      setAddPhoneError('Phone number must be 10 digits');
      return false;
    }

    setAddPhoneError('');
    return true;
  };

  const fetchInvitations = async () => {
    try {
      const { data, error } = await supabase
        .from('coach_invitations')
        .select('*')
        .eq('team_id', currentTeam?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInvitations(data || []);
    } catch (error) {
      console.error('Error fetching invitations:', error);
      toast.error('Failed to fetch invitations');
    }
  };

  const fetchCoaches = async () => {
    try {
      const { data, error } = await supabase
        .from('coaches')
        .select('*')
        .eq('team_id', currentTeam?.id)
        .order('name');

      if (error) throw error;
      
      // Initialize coaches without PII data
      const coachesWithoutPII = data || [];
      // Defensive dedupe in case legacy duplicate rows already exist.
      const dedupedCoaches = coachesWithoutPII.filter((coach, index, arr) => {
        const key = coach.user_id || `${coach.name}|${coach.role}|${coach.team_id}`;
        return arr.findIndex(c => (c.user_id || `${c.name}|${c.role}|${c.team_id}`) === key) === index;
      });
      setCoaches(dedupedCoaches);
      
      // Fetch PII data for each coach
      for (const coach of dedupedCoaches) {
        try {
          const piiData = await piiService.getCoachPII(coach.id);
          if (piiData) {
            setCoaches(prevCoaches => 
              prevCoaches.map(c => 
                c.id === coach.id 
                  ? { ...c, email: piiData.email || '', phone: piiData.phone || '' } 
                  : c
              )
            );
          }
        } catch (piiError) {
          console.error(`Error fetching PII for coach ${coach.id}:`, piiError);
        }
      }
    } catch (error) {
      console.error('Error fetching coaches:', error);
      toast.error('Failed to fetch coaches');
    }
  };

  const handleInviteCoach = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate inputs
    const isEmailValid = validateEmail(email);
    const isPhoneValid = validatePhone(phone);
    
    if (!isEmailValid || !isPhoneValid) {
      return; // Stop if validation fails
    }
    
    if (!email || !currentTeam?.id || !user) return;

    setIsInviting(true);
    let createdInvitation = false;
    try {
      // Check if invitation already exists (regardless of status)
      const { data: existingInvitations, error: invitationError } = await supabase
        .from('coach_invitations')
        .select('id, status')
        .eq('team_id', currentTeam.id)
        .eq('email', email);

      if (invitationError) throw invitationError;

      // Check for pending invitation
      const pendingInvitation = existingInvitations?.find(inv => inv.status === 'pending');
      if (pendingInvitation) {
        toast.error('This person already has a pending invitation. You can cancel it and send a new one.');
        setIsInviting(false);
        return;
      }
      
      // Map role to position for database compatibility
      const position = role === 'head_coach' ? 'Head Coach' : 'Assistant Coach';
      
      // If there are existing invitations (rejected or accepted), delete them first to avoid constraint conflicts
      if (existingInvitations && existingInvitations.length > 0) {
        const { error: deleteError } = await supabase
          .from('coach_invitations')
          .delete()
          .eq('team_id', currentTeam.id)
          .eq('email', email);
          
        if (deleteError) throw deleteError;
      }
      
      // Create new invitation
      const { data: newInvitation, error: createError } = await supabase
        .from('coach_invitations')
        .insert({
          team_id: currentTeam.id,
          email,
          role,
          status: 'pending',
          invited_by: user.id,
          phone: phone || null,
          position: position
        })
        .select()
        .single();

      if (createError) throw createError;
      createdInvitation = true;

      // Send invitation email
      const { error: emailError } = await supabase.functions.invoke('send-coach-invitation', {
        body: { 
          email, 
          teamName: currentTeam.name, 
          role,
          phone,
          invitationId: newInvitation.id
        }
      });

      if (emailError) throw emailError;

      toast.success('Invitation sent successfully');
      setEmail('');
      setPhone('');
      fetchInvitations();
    } catch (error) {
      console.error('Error sending invitation:', error);
      const message = await getInviteErrorMessage(error);
      toast.error(createdInvitation ? message : 'Failed to create invitation');
      fetchInvitations();
    } finally {
      setIsInviting(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from('coach_invitations')
        .delete()
        .eq('id', invitationId);

      if (error) throw error;

      // Update local state immediately
      setInvitations(prevInvitations => 
        prevInvitations.filter(inv => inv.id !== invitationId)
      );

      toast.success('Invitation cancelled');
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      toast.error('Failed to cancel invitation');
    }
  };

  const handleRemoveCoach = async (coachId: string) => {
    if (!window.confirm('Are you sure you want to remove this coach?')) return;

    try {
      const { error } = await supabase
        .from('coaches')
        .delete()
        .eq('id', coachId);

      if (error) throw error;

      toast.success('Coach removed successfully');
      fetchCoaches();
    } catch (error) {
      console.error('Error removing coach:', error);
      toast.error('Failed to remove coach');
    }
  };

  const handleEditCoach = async (coachId: string) => {
    try {
      const coach = coaches.find(c => c.id === coachId);
      if (!coach) return;

      let resolvedEmail = coach.email || '';
      let resolvedPhone = coach.phone || '';

      // Always fetch latest encrypted PII first (covers manually-added coaches).
      try {
        const piiData = await piiService.getCoachPII(coach.id);
        if (piiData) {
          resolvedEmail = piiData.email || resolvedEmail;
          resolvedPhone = piiData.phone || resolvedPhone;
        }
      } catch (piiError) {
        console.warn(`Unable to fetch PII for coach ${coach.id} while opening edit modal:`, piiError);
      }

      // For invited coaches, email can come from profile/coach_invitations if encrypted PII is empty.
      if (!resolvedEmail && coach.user_id) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('email')
          .eq('id', coach.user_id)
          .maybeSingle();
        resolvedEmail = profileData?.email || resolvedEmail;
      }

      // Phone for invited coaches may only exist on invitation history.
      if (currentTeam?.id && (resolvedEmail || !resolvedPhone)) {
        const invitationQuery = supabase
          .from('coach_invitations')
          .select('email, phone, created_at')
          .eq('team_id', currentTeam.id)
          .order('created_at', { ascending: false })
          .limit(1);

        const { data: invitationData } = resolvedEmail
          ? await invitationQuery.ilike('email', resolvedEmail)
          : await invitationQuery;

        if (invitationData && invitationData.length > 0) {
          resolvedEmail = resolvedEmail || invitationData[0].email || '';
          resolvedPhone = resolvedPhone || invitationData[0].phone || '';
        }
      }

      setEditForm({
        name: coach.name,
        email: resolvedEmail,
        phone: resolvedPhone,
        role: coach.role
      });
      
      // Clear any previous errors
      setEditEmailError('');
      setEditPhoneError('');
      
      setEditingCoach(coachId);
    } catch (error) {
      console.error('Error preparing coach edit:', error);
      toast.error('Failed to prepare coach edit');
    }
  };

  const handleSaveCoachEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate inputs
    const isEmailValid = validateEditEmail(editForm.email);
    const isPhoneValid = validateEditPhone(editForm.phone);
    
    if (!isEmailValid || !isPhoneValid) {
      return; // Stop if validation fails
    }
    
    if (!editingCoach || !currentTeam) return;
    
    try {
      // Map role to position for database compatibility
      const position = editForm.role === 'head_coach' ? 'Head Coach' : 'Assistant Coach';
      
      // Update coach basic info
      const { error } = await supabase
        .from('coaches')
        .update({
          name: editForm.name,
          role: editForm.role,
          position: position
        })
        .eq('id', editingCoach);

      if (error) throw error;
      
      // Update PII information
      await piiService.updateCoachPII(
        editingCoach,
        editForm.phone,
        editForm.email
      );
      
      toast.success('Coach updated successfully');
      setEditingCoach(null);
      fetchCoaches();
    } catch (error) {
      console.error('Error updating coach:', error);
      toast.error('Failed to update coach');
    }
  };

  const handleAddCoach = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate inputs
    const isEmailValid = validateAddEmail(addCoachForm.email);
    const isPhoneValid = validateAddPhone(addCoachForm.phone);
    
    if (!isEmailValid || !isPhoneValid) {
      return; // Stop if validation fails
    }
    
    if (!currentTeam) return;
    
    try {
      // Map role to position for database compatibility
      const position = addCoachForm.role === 'head_coach' ? 'Head Coach' : 'Assistant Coach';
      
      // Create new coach
      const { data, error } = await supabase
        .from('coaches')
        .insert({
          team_id: currentTeam.id,
          name: addCoachForm.name,
          role: addCoachForm.role,
          position: position
        })
        .select();

      if (error) throw error;
      
      if (data && data.length > 0) {
        // Update PII data
        await piiService.updateCoachPII(
          data[0].id,
          addCoachForm.phone,
          addCoachForm.email
        );
        
        toast.success('Coach added successfully');
        setAddCoachForm({
          name: '',
          email: '',
          phone: '',
          role: 'assistant_coach'
        });
        setAddEmailError('');
        setAddPhoneError('');
        fetchCoaches();
      }
    } catch (error) {
      console.error('Error adding coach:', error);
      toast.error('Failed to add coach');
    }
  };

  const resetEditForm = () => {
    setEditForm({
      name: '',
      email: '',
      phone: '',
      role: 'assistant_coach'
    });
    setEditEmailError('');
    setEditPhoneError('');
  };

  const formatPhoneInput = (value: string) => {
    // Remove any non-digits
    const digits = value.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (value: string, isEditForm: boolean = false) => {
    const formattedValue = formatPhoneInput(value);
    if (isEditForm) {
      setEditForm({...editForm, phone: formattedValue});
      validateEditPhone(formattedValue);
    } else {
      setPhone(formattedValue);
      validatePhone(formattedValue);
    }
  };

  const handleAddCoachPhoneChange = (value: string) => {
    const formattedValue = formatPhoneInput(value);
    setAddCoachForm({ ...addCoachForm, phone: formattedValue });
    validateAddPhone(formattedValue);
  };

  const handleEmailChange = (value: string, isEditForm: boolean = false) => {
    if (isEditForm) {
      setEditForm({...editForm, email: value});
      validateEditEmail(value);
    } else {
      setEmail(value);
      validateEmail(value);
    }
  };

  const handleAddCoachEmailChange = (value: string) => {
    setAddCoachForm({ ...addCoachForm, email: value });
    validateAddEmail(value);
  };

  return (
    <div className="space-y-3">
      {/* Add Coach Form */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="text-lg font-semibold mb-3">Add Coach Directly</h3>
        <form onSubmit={handleAddCoach} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <Input
              type="text"
              value={addCoachForm.name}
              onChange={(e) => setAddCoachForm({...addCoachForm, name: e.target.value})}
              placeholder="Enter coach's name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <Input
              type="email"
              value={addCoachForm.email}
              onChange={(e) => handleAddCoachEmailChange(e.target.value)}
              placeholder="Enter coach's email"
              required
              className={addEmailError ? "border-red-500" : ""}
            />
            {addEmailError && <p className="text-red-500 text-xs mt-1">{addEmailError}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <Input
              type="tel"
              value={addCoachForm.phone}
              onChange={(e) => handleAddCoachPhoneChange(e.target.value)}
              placeholder="(555) 555-5555"
              className={addPhoneError ? "border-red-500" : ""}
            />
            {addPhoneError && <p className="text-red-500 text-xs mt-1">{addPhoneError}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <MobileSelect
              value={addCoachForm.role}
              onChange={(v) => setAddCoachForm({...addCoachForm, role: v as 'head_coach' | 'assistant_coach'})}
              options={[
                { value: 'assistant_coach', label: 'Assistant Coach' },
                { value: 'head_coach', label: 'Head Coach' },
              ]}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              aria-label="Role"
              sheetTitle="Select Role"
            />
          </div>
          <Button
            type="submit"
            className="inline-flex items-center justify-center"
            size="md"
          >
            <UserPlus className="w-4 h-4 mr-1" />
            Add Coach
          </Button>
        </form>
      </div>

      {/* Invite Coach Form */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="text-lg font-semibold mb-3">Invite Coach</h3>
        <form onSubmit={handleInviteCoach} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              placeholder="Enter coach's email"
              required
              className={emailError ? "border-red-500" : ""}
            />
            {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder="(555) 555-5555"
              className={phoneError ? "border-red-500" : ""}
            />
            {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <MobileSelect
              value={role}
              onChange={(v) => setRole(v as 'head_coach' | 'assistant_coach')}
              options={[
                { value: 'assistant_coach', label: 'Assistant Coach' },
                { value: 'head_coach', label: 'Head Coach' },
              ]}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              aria-label="Role"
              sheetTitle="Select Role"
            />
          </div>
          <Button
            type="submit"
            disabled={isInviting}
            className="inline-flex items-center justify-center"
            size="md"
          >
            {isInviting ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Sending Invitation...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-1" />
                Send Invitation
              </>
            )}
          </Button>
        </form>
      </div>

      {/* Current Coaches */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="text-lg font-semibold mb-3">Current Coaches</h3>
        
        {/* Edit Coach Modal */}
        {editingCoach && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-md p-6">
              <h3 className="text-xl font-bold mb-4">Edit Coach</h3>
              <form onSubmit={handleSaveCoachEdit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <Input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    placeholder="Enter coach's name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => handleEmailChange(e.target.value, true)}
                    placeholder="Enter coach's email"
                    className={editEmailError ? "border-red-500" : ""}
                    required
                  />
                  {editEmailError && <p className="text-red-500 text-xs mt-1">{editEmailError}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <Input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => handlePhoneChange(e.target.value, true)}
                    placeholder="(555) 555-5555"
                    className={editPhoneError ? "border-red-500" : ""}
                  />
                  {editPhoneError && <p className="text-red-500 text-xs mt-1">{editPhoneError}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <MobileSelect
                    value={editForm.role}
                    onChange={(v) => setEditForm({...editForm, role: v as 'head_coach' | 'assistant_coach'})}
                    options={[
                      { value: 'assistant_coach', label: 'Assistant Coach' },
                      { value: 'head_coach', label: 'Head Coach' },
                    ]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    aria-label="Role"
                    sheetTitle="Select Role"
                  />
                </div>
                <div className="flex justify-end space-x-2 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingCoach(null);
                      resetEditForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    size="sm"
                    className="inline-flex items-center"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Save Changes
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        <div className="divide-y">
          {coaches.map((coach) => (
            <div
              key={coach.id}
              className="py-3 flex items-center justify-between"
            >
              <div>
                <div className="font-medium">{coach.name}</div>
                <div className="text-sm text-gray-500">{coach.email}</div>
                <div className="text-sm text-gray-500">{coach.phone}</div>
                <div className="text-sm text-gray-500 capitalize">
                  {coach.role === 'head_coach' ? 'Head Coach' : 'Assistant Coach'}
                </div>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="inline-flex items-center p-1"
                  onClick={() => handleEditCoach(coach.id)}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="inline-flex items-center p-1"
                  onClick={() => handleRemoveCoach(coach.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          {coaches.length === 0 && (
            <div className="text-center text-gray-500 py-3">
              No coaches have been added yet
            </div>
          )}
        </div>
      </div>

      {/* Pending Invitations */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="text-lg font-semibold mb-3">Pending Invitations</h3>
        <div className="space-y-3">
          {invitations
            .filter((inv) => inv.status === 'pending')
            .map((invitation) => (
              <div
                key={invitation.id}
                className="p-3 border rounded-lg"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="font-medium">{invitation.email}</div>
                    <div className="text-sm text-gray-500 capitalize">
                      {invitation.role === 'head_coach' ? 'Head Coach' : 'Assistant Coach'}
                    </div>
                    <div className="text-sm text-gray-500">
                      Sent {new Date(invitation.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    className="inline-flex items-center"
                    onClick={() => handleCancelInvitation(invitation.id)}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ))}
          {invitations.filter((inv) => inv.status === 'pending').length === 0 && (
            <div className="text-center text-gray-500 py-3">
              No pending invitations
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 